import React, { useState, useEffect, useCallback, useRef } from 'react';
import { WifiOff, RefreshCw, CheckCircle2 } from 'lucide-react';
import api from '../services/api';

interface NetworkStatusBannerProps {
  onReconnect?: () => void;
}

export const NetworkStatusBanner: React.FC<NetworkStatusBannerProps> = ({ onReconnect }) => {
  const [isOffline, setIsOffline] = useState(() => !navigator.onLine);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [justReconnected, setJustReconnected] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [attemptCount, setAttemptCount] = useState(1);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const testConnection = useCallback(async (): Promise<boolean> => {
    try {
      setIsReconnecting(true);
      const res = await api.get('/api/garcom/ping', { timeout: 3000 });
      if (res.status === 200 && res.data?.success) {
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      setIsReconnecting(false);
    }
  }, []);

  const triggerReconnect = useCallback(async () => {
    const success = await testConnection();
    if (success) {
      setIsOffline(false);
      setJustReconnected(true);
      setAttemptCount(1);
      setCountdown(5);

      if (onReconnect) {
        onReconnect();
      }

      setTimeout(() => {
        setJustReconnected(false);
      }, 3500);
    } else {
      setIsOffline(true);
      setAttemptCount(prev => (prev < 99 ? prev + 1 : 1));
      setCountdown(5);
    }
  }, [testConnection, onReconnect]);

  // Contagem regressiva e loop de reconexão
  useEffect(() => {
    if (!isOffline) return;

    timerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          triggerReconnect();
          return 5;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isOffline, triggerReconnect]);

  // Heartbeat periódico de fundo (a cada 45 segundos) para manter o canal de rede aquecido
  useEffect(() => {
    const heartbeatInterval = setInterval(async () => {
      if (!isOffline && document.visibilityState === 'visible') {
        const ok = await testConnection();
        if (!ok) {
          setIsOffline(true);
          setCountdown(5);
        }
      }
    }, 45000);

    return () => clearInterval(heartbeatInterval);
  }, [isOffline, testConnection]);

  // Listener para despertar de tela / desbloqueio do celular (visibilitychange / focus)
  useEffect(() => {
    const handleWakeup = async () => {
      if (document.visibilityState === 'visible') {
        console.log('⚡ [NetworkStatusBanner] Celular/Aba despertou! Testando conexão imediatamente...');
        const ok = await testConnection();
        if (ok) {
          if (isOffline) {
            triggerReconnect();
          } else if (onReconnect) {
            onReconnect();
          }
        } else {
          setIsOffline(true);
          setCountdown(5);
        }
      }
    };

    document.addEventListener('visibilitychange', handleWakeup);
    window.addEventListener('focus', handleWakeup);

    return () => {
      document.removeEventListener('visibilitychange', handleWakeup);
      window.removeEventListener('focus', handleWakeup);
    };
  }, [isOffline, testConnection, triggerReconnect, onReconnect]);

  // Listeners do navegador para status de rede
  useEffect(() => {
    const handleOnline = () => {
      triggerReconnect();
    };

    const handleOffline = () => {
      setIsOffline(true);
      setCountdown(5);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [triggerReconnect]);

  if (justReconnected) {
    return (
      <div className="bg-emerald-600 text-white px-4 py-2 text-center flex items-center justify-center gap-2 sticky top-0 z-[100] shadow-md animate-in slide-in-from-top duration-300">
        <CheckCircle2 className="h-4 w-4 text-emerald-200 flex-shrink-0 animate-bounce" />
        <p className="text-xs sm:text-sm font-semibold">
          Conexão restabelecida com sucesso! Dados atualizados.
        </p>
      </div>
    );
  }

  if (!isOffline) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-amber-600 to-yellow-600 text-white px-3 sm:px-4 py-2.5 text-center flex items-center justify-between gap-2 sticky top-0 z-[100] shadow-lg border-b border-amber-400/40">
      <div className="flex items-center gap-2 min-w-0">
        <WifiOff className="h-4 w-4 text-yellow-200 flex-shrink-0 animate-pulse" />
        <p className="text-xs sm:text-sm font-semibold truncate text-left">
          <span className="hidden sm:inline">Tentando reconectar automaticamente em </span>
          <span className="sm:hidden">Reconectando em </span>
          <span className="font-extrabold text-yellow-100">{countdown}s</span>
          <span className="text-xs opacity-90 ml-1">({attemptCount}ª tentativa)</span>
        </p>
      </div>

      <button
        onClick={triggerReconnect}
        disabled={isReconnecting}
        className="bg-black/30 hover:bg-black/40 text-yellow-100 px-3 py-1 rounded-lg text-xs font-bold transition-all active:scale-95 flex items-center gap-1.5 flex-shrink-0 border border-yellow-300/30 touch-manipulation disabled:opacity-50"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${isReconnecting ? 'animate-spin' : ''}`} />
        <span>{isReconnecting ? 'Testando...' : 'Reconectar Agora'}</span>
      </button>
    </div>
  );
};

export default NetworkStatusBanner;
